import axios from 'axios';

import type { CountryCode } from '@apple/utils/globalization/models';

import type {
	UserCountryAssociation,
	UserPlantAssociation,
} from '@apple/features/user/models/associations';

import type {
	UserFormData,
	UserManagementOptions,
	UserMarketAssociation,
	UserModel,
	UserStoreAssociation,
} from '../models/management';
import {
	userFormDataSchema,
	userManagementOptionsSchema,
	userModelSchema,
} from '../models/management';

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function getUser(username: string, signal?: AbortSignal): Promise<UserFormData> {
	const { data } = await axios.get<unknown>('/api/management/users', {
		params: { username },
		signal,
	});

	const result = userModelSchema.parse(data);

	return getUserFormData(result);
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function saveUser(user: UserFormData): Promise<void> {
	const model = getUserModel(user);
	await axios.post('/api/management/users/save', model);
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function addUser(user: UserFormData): Promise<void> {
	const model = getUserModel(user);
	await axios.post('/api/management/users/add', model);
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function unlockUser(username: string): Promise<void> {
	await axios.post(`/api/management/users/${encodeURIComponent(username)}/unlock`);
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function deleteUser(username: string): Promise<void> {
	await axios.delete(`/api/management/users/${encodeURIComponent(username)}/`);
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function deleteUsers(usernames: string[], auditComment?: string): Promise<void> {
	if (usernames.length === 0) {
		return;
	}

	await axios.post('/api/management/users/delete-multiple', usernames, {
		headers: auditComment ? { ChangeComment: auditComment } : undefined,
	});
}

/** @see AppleRetail.Web.Features.Profiles.UserManagementController */
export async function getUserManagementOptions(
	signal?: AbortSignal,
): Promise<UserManagementOptions> {
	const { data } = await axios.get<unknown>('/api/management/users/data', { signal });
	return userManagementOptionsSchema.parse(data);
}

/** Gets the user form data from the user model expected by the management API.
 * Some properties are named differently in the server validation model and are mapped.
 * Associations are filtered by type into separate properties.
 */
export function getUserFormData(user: UserModel): UserFormData {
	return userFormDataSchema.parse({
		...user,
		emailAddress: user.email,
		defaultCountry: user.countryCode ?? '',
		userRole: user.userRole ?? '',
		allAssociation: user.userAssociations.some(x => x.accessType === 'All')
			? { userId: user.id }
			: null,
		plantAssociations: user.userAssociations
			.filter(x => x.accessType === 'Plant')
			.map<UserPlantAssociation>(x => ({
				plantId: Number(x.primaryAssociation),
				userId: user.id,
			})),
		countryAssociations: user.userAssociations
			.filter(x => x.accessType === 'Country')
			.map<UserCountryAssociation>(x => ({
				countryCode: x.primaryAssociation as CountryCode,
				userId: user.id,
			})),
		marketAssociations: user.userAssociations
			.filter(x => x.accessType === 'CustomerGroup')
			.map<UserMarketAssociation>(x => ({
				customerGroupId: Number(x.primaryAssociation),
				userId: user.id,
			})),
		storeAssociations: user.userAssociations
			.filter(x => x.accessType === 'Customer')
			.map<UserStoreAssociation>(x => ({
				customerId: Number(x.primaryAssociation),
				userId: user.id,
			})),
	});
}

/** Gets the user model expected by the management API from user form data.
 * Some properties are named differently in the server validation model and are mapped.
 * Associations are stored in a single array of user associations.
 */
export function getUserModel(user: UserFormData): UserModel {
	return userModelSchema.parse({
		...user,
		email: user.emailAddress,
		countryCode: user.defaultCountry,
		userAssociations: [
			...(user.allAssociation ? [{ accessType: 'All', primaryAssociation: '0' }] : []),
			...user.plantAssociations.map((x: UserPlantAssociation) => ({
				accessType: 'Plant',
				primaryAssociation: x.plantId.toString(),
			})),
			...user.countryAssociations.map((x: UserCountryAssociation) => ({
				accessType: 'Country',
				primaryAssociation: x.countryCode,
			})),
			...user.marketAssociations.map((x: UserMarketAssociation) => ({
				accessType: 'CustomerGroup',
				primaryAssociation: x.customerGroupId.toString(),
			})),
			...user.storeAssociations.map((x: UserStoreAssociation) => ({
				accessType: 'Customer',
				primaryAssociation: x.customerId.toString(),
			})),
		],
	});
}
